import { Container, SimpleGrid, Stack } from '@mantine/core';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PmTaskCategory } from '@/api/generated/models/pmTaskCategory';
import { useGetPmDashboardSummary } from '@/api/generated/pm-dashboard/pm-dashboard';
import { IconFigmaCalendar } from '@/assets/icons';
import { DateRangeField, PageHeading, SelectField } from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { BoardKpiCard } from '@/features/board/components/BoardKpiCard';
import { InfoBulletList } from '@/features/board/components/PanelInfoIcon';
import { PmMobileFilters } from '@/features/dashboard/components/PmMobileFilters';
import { PmTaskTable } from '@/features/dashboard/components/PmTaskTable';
import { RecentActivityList } from '@/features/dashboard/components/RecentActivityList';
import {
  buildPmPeriodOptions,
  buildPmStatusFilterOptions,
  resolvePmPeriodSince
} from '@/features/dashboard/pmDashboardDisplay';
import { formatDateRange, toIsoDateString } from '@/utils/format';

export const PmDashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const [period, setPeriod] = useState<string | null>('ALL');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [statusFilter, setStatusFilter] = useState<string | null>('ALL');
  const isCustomPeriod = period === 'CUSTOM';

  const periodSince = isCustomPeriod ? undefined : resolvePmPeriodSince(period, new Date());
  const dueFrom = isCustomPeriod
    ? (dateRange[0] ?? undefined)
    : periodSince && toIsoDateString(periodSince);
  const dueUntil = isCustomPeriod ? (dateRange[1] ?? undefined) : undefined;

  // refetchOnMount: 'always' — "Moje stavke"/"Nedavna aktivnost" moraju biti svježe svaki put
  // kad se uđe na nadzornu ploču, ne tek kad istekne globalni 5-minutni staleTime (npr. ugovor
  // kreiran iz ponude mora se odmah pojaviti, ne tek nakon ručnog refresha).
  const { data } = useGetPmDashboardSummary(
    { dueFrom, dueUntil },
    { query: { refetchOnMount: 'always' } }
  );

  const periodOptions = buildPmPeriodOptions(t);
  const statusOptions = buildPmStatusFilterOptions(t);
  const isFilterActive = period !== 'ALL' || statusFilter !== 'ALL';
  const resetFilters = () => {
    setPeriod('ALL');
    setDateRange([null, null]);
    setStatusFilter('ALL');
  };
  const periodSummary = isCustomPeriod
    ? dateRange[0] || dateRange[1]
      ? formatDateRange(dateRange[0] ?? undefined, dateRange[1] ?? undefined)
      : periodOptions.find((option) => option.value === period)?.label
    : periodOptions.find((option) => option.value === period)?.label;
  const statusSummary = statusOptions.find((option) => option.value === statusFilter)?.label;
  const filterSummary = [
    periodSummary && `${t('pmDashboard.filterPeriod')}: ${periodSummary}`,
    statusSummary && `${t('pmDashboard.filterStatus')}: ${statusSummary}`
  ]
    .filter(Boolean)
    .join(' · ');

  const taskListRef = useRef<HTMLDivElement>(null);
  const openStatusTab = (category: PmTaskCategory) => {
    setStatusFilter(category);
    taskListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const tasks = useMemo(
    () =>
      (data?.tasks ?? []).filter(
        (task) => statusFilter === 'ALL' || task.category === statusFilter
      ),
    [data?.tasks, statusFilter]
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <PageHeading
          title={t('pmDashboard.greeting', { name: user?.firstName ?? '' })}
          subtitle={t('pmDashboard.subtitle')}
        />

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={{ base: 12, sm: 16 }}>
          <BoardKpiCard
            label={t('pmDashboard.kpiTodo')}
            value={data?.counts?.todo ?? 0}
            onClick={() => openStatusTab(PmTaskCategory.TODO)}
            infoWidth={300}
            info={
              <InfoBulletList
                intro={t('pmDashboard.kpiTodoInfoIntro')}
                items={[
                  t('pmDashboard.kpiTodoInfoOfferDraft'),
                  t('pmDashboard.kpiTodoInfoOfferAccepted'),
                  t('pmDashboard.kpiTodoInfoContractAccepted'),
                  t('pmDashboard.kpiTodoInfoSendReport'),
                  t('pmDashboard.kpiTodoInfoCorrectReport'),
                  t('pmDashboard.kpiTodoInfoContractNotStarted'),
                  t('pmDashboard.kpiTodoInfoNoRealization')
                ]}
              />
            }
          />
          <BoardKpiCard
            label={t('pmDashboard.kpiReview')}
            value={data?.counts?.review ?? 0}
            onClick={() => openStatusTab(PmTaskCategory.REVIEW)}
            infoWidth={280}
            info={
              <InfoBulletList
                intro={t('pmDashboard.kpiReviewInfoIntro')}
                items={[
                  t('pmDashboard.kpiReviewInfoWaiting'),
                  t('pmDashboard.kpiReviewInfoBelowPlan')
                ]}
              />
            }
          />
          <BoardKpiCard
            label={t('pmDashboard.kpiInProgress')}
            value={data?.counts?.inProgress ?? 0}
            onClick={() => openStatusTab(PmTaskCategory.IN_PROGRESS)}
            infoWidth={260}
            info={
              <InfoBulletList
                intro={t('pmDashboard.kpiInProgressInfoIntro')}
                items={[t('pmDashboard.kpiInProgressInfoItem')]}
              />
            }
          />
          <BoardKpiCard
            label={t('pmDashboard.kpiDone')}
            value={data?.counts?.done ?? 0}
            onClick={() => openStatusTab(PmTaskCategory.DONE)}
            infoWidth={290}
            info={
              <InfoBulletList
                intro={t('pmDashboard.kpiDoneInfoIntro')}
                items={[
                  t('pmDashboard.kpiDoneInfoReportApproved'),
                  t('pmDashboard.kpiDoneInfoContractActivated'),
                  t('pmDashboard.kpiDoneInfoContractFromOffer')
                ]}
              />
            }
          />
        </SimpleGrid>

        <div ref={taskListRef} style={{ scrollMarginTop: 72 }}>
          <PmTaskTable
            title={t('pmDashboard.table.title')}
            info={
              <Stack gap={10}>
                <InfoBulletList
                  intro={t('pmDashboard.table.infoIntro')}
                  items={[
                    t('pmDashboard.table.infoStatus'),
                    t('pmDashboard.table.infoDateFilter'),
                    t('pmDashboard.table.infoNoDueDate'),
                    t('pmDashboard.table.infoDismiss')
                  ]}
                />
                <InfoBulletList
                  intro={t('pmDashboard.table.infoSortIntro')}
                  items={[
                    t('pmDashboard.table.infoSortDate'),
                    t('pmDashboard.table.infoSortUndated'),
                    t('pmDashboard.table.infoSortPriority')
                  ]}
                />
              </Stack>
            }
            tasks={tasks}
            mobileFilterSummary={filterSummary}
            desktopFilters={
              <>
                <div style={{ width: 200 }}>
                  <SelectField
                    hideLabel
                    label={t('pmDashboard.filterPeriod')}
                    data={periodOptions}
                    value={period}
                    onChange={(value) => {
                      setPeriod(value);
                      if (value !== 'CUSTOM') setDateRange([null, null]);
                    }}
                    allowDeselect={false}
                    leftSection={<IconFigmaCalendar size={16} />}
                  />
                </div>
                {isCustomPeriod && (
                  <div style={{ width: 240 }}>
                    <DateRangeField
                      hideLabel
                      label={t('pmDashboard.filterDateRange')}
                      hideCalendarIcon
                      placeholder={t('pmDashboard.filterDateRange')}
                      valueFormat="DD.MM.YYYY."
                      value={dateRange}
                      onChange={setDateRange}
                      clearable
                    />
                  </div>
                )}
                <div style={{ width: 180 }}>
                  <SelectField
                    hideLabel
                    label={t('pmDashboard.filterStatus')}
                    data={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    allowDeselect={false}
                  />
                </div>
              </>
            }
            mobileFilters={
              <PmMobileFilters
                periodOptions={periodOptions}
                period={period}
                onPeriodChange={(value) => {
                  setPeriod(value);
                  if (value !== 'CUSTOM') setDateRange([null, null]);
                }}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                statusOptions={statusOptions}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                isActive={isFilterActive}
                onReset={resetFilters}
                resultCount={tasks.length}
              />
            }
          />
        </div>

        <RecentActivityList
          title={t('pmDashboard.activity.title')}
          info={
            <InfoBulletList
              intro={t('pmDashboard.activity.infoIntro')}
              items={[
                t('pmDashboard.activity.infoOfferAccepted'),
                t('pmDashboard.activity.infoContractCreatedFromOffer'),
                t('pmDashboard.activity.infoContractActivated'),
                t('pmDashboard.activity.infoReportSubmitted'),
                t('pmDashboard.activity.infoReportRejected'),
                t('pmDashboard.activity.infoReportApproved'),
                t('pmDashboard.activity.infoInvoicePaid')
              ]}
            />
          }
          items={data?.recentActivity ?? []}
        />
      </Stack>
    </Container>
  );
};
